import { useToolOwnership } from "./call/shared/toolOwnership"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, Eye, EyeOff, MessageSquareText, Play, RefreshCw, Share2, Shuffle, Sparkles, Square, X } from "lucide-react"

import { Button } from "components/ui/button"
import { Textarea } from "components/ui/textarea"
import { meetings } from "services/api/meetingsClient"
import type { MeetingInspireActivity, MeetingInspireDeck, MeetingInspireResponse } from "services/api/meetingsClient"
import {
  INSPIRE_CONTROL_MESSAGE_TYPE,
  isInspireControlAckMessage,
  type InspireControlAction,
} from "./inspireControlMessages"
import { useMeetingPanelEvents } from "./useMeetingPanelEvents"
import type { InspireEmbedContext } from "./call/types"

const POLL_MS = 30000
const ACK_TIMEOUT_MS = 3000
const CUSTOM_DECK_ID = "__custom__"
const CUSTOM_CARD_ACCENT = "#F7D58A"

const cardInk = (hex: string) => {
  const normalized = hex.replace("#", "")
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return "#172033"
  const [red, green, blue] = [0, 2, 4].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16))
  return (red * 299 + green * 587 + blue * 114) / 1000 > 145 ? "#172033" : "#fffdf8"
}

export type WiseFriendControlsLaunchSelection =
  | { kind: "card"; deckId: string; cardId: string; visibility: "named" | "anonymous" }
  | { kind: "custom"; customPrompt: string; visibility: "named" | "anonymous" }

export type WiseFriendControlsPreview = {
  decks: MeetingInspireDeck[]
  active: MeetingInspireActivity | null
  activities?: MeetingInspireActivity[]
  latestActivity?: MeetingInspireActivity | null
  responses?: MeetingInspireResponse[]
  responsesByActivity?: Record<string, MeetingInspireResponse[]>
  sharedResponse?: MeetingInspireResponse | null
  sharedActivityId?: string | null
  responseCount: number
  onLaunch: (selection: WiseFriendControlsLaunchSelection) => void
  onEnd: () => void
  onShareResponse?: (response: MeetingInspireResponse, activity: MeetingInspireActivity) => void
  onStopSharing?: () => void
}

function ControlsSession({ preview, embed }: { preview?: WiseFriendControlsPreview; embed?: InspireEmbedContext }) {
  const { meetingId: routeMeetingId } = useParams<{ meetingId: string }>()
  const [searchParams] = useSearchParams()
  const meetingId = embed?.meetingId || routeMeetingId
  const panelToken = embed?.panelToken || searchParams.get("panel_token") || ""
  const ownership = useToolOwnership(`${meetingId}:${panelToken}`)
  const captureTool = ownership.capture
  const [decks, setDecks] = useState<MeetingInspireDeck[]>(preview?.decks || [])
  const [deckId, setDeckId] = useState("")
  const [cardId, setCardId] = useState("")
  const [customPrompt, setCustomPrompt] = useState("")
  const [visibility, setVisibility] = useState<"named" | "anonymous">("named")
  const [active, setActive] = useState<MeetingInspireActivity | null>(null)
  const [latestActivity, setLatestActivity] = useState<MeetingInspireActivity | null>(preview?.latestActivity || preview?.active || null)
  const [activities, setActivities] = useState<MeetingInspireActivity[]>(preview?.activities || (preview?.latestActivity ? [preview.latestActivity] : []))
  const [selectedReviewActivityId, setSelectedReviewActivityId] = useState(preview?.latestActivity?.id || preview?.active?.id || "")
  const [responses, setResponses] = useState<MeetingInspireResponse[]>(preview?.responses || [])
  const [sharedResponse, setSharedResponse] = useState<MeetingInspireResponse | null>(preview?.sharedResponse || null)
  const [sharedActivityId, setSharedActivityId] = useState<string | null>(preview?.sharedActivityId || (preview?.sharedResponse ? preview?.latestActivity?.id || null : null))
  const [shuffleCycle, setShuffleCycle] = useState(0)
  const [isShuffling, setIsShuffling] = useState(false)
  const [responseCount, setResponseCount] = useState(0)
  const [nextPage, setNextPage] = useState<number | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingReview, setLoadingReview] = useState(false)
  const [showReview, setShowReview] = useState(Boolean(preview?.latestActivity && !preview?.active))
  const [loading, setLoading] = useState(!preview && Boolean(meetingId && panelToken))
  const [pending, setPending] = useState<InspireControlAction | null>(null)
  const [error, setError] = useState(preview || (meetingId && panelToken) ? "" : "This WiseFriend controls link is unavailable.")
  /** A poll was still open at launch: the presenter can close it and deal the card in one step. */
  const [confirmClosePoll, setConfirmClosePoll] = useState(false)
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null)
  const ackTimerRef = useRef<number | null>(null)
  const loadRef = useRef(false)
  const pendingLoadRef = useRef(false)
  const dismissedReviewIdRef = useRef<string | null>(null)
  const selectedReviewActivityIdRef = useRef(selectedReviewActivityId)
  const shuffleTimerRef = useRef<number | null>(null)

  const availableDecks = preview?.decks || decks
  const isCustom = deckId === CUSTOM_DECK_ID || availableDecks.length === 0
  const selectedDeck = useMemo(
    () => isCustom ? null : availableDecks.find((deck) => deck.id === deckId) || availableDecks[0] || null,
    [availableDecks, deckId, isCustom],
  )
  const selectedCard = useMemo(
    () => isCustom ? null : selectedDeck?.cards.find((card) => card.id === cardId) || selectedDeck?.cards[0] || null,
    [cardId, isCustom, selectedDeck],
  )
  const selectedAccent = isCustom ? CUSTOM_CARD_ACCENT : selectedDeck?.accent_color || "#1F3A5F"
  const selectedInk = cardInk(selectedAccent)
  const currentActive = preview?.active || active
  const currentLatestActivity = preview?.latestActivity || latestActivity
  const sessionActivities = preview?.activities || (preview?.latestActivity ? [preview.latestActivity] : activities)
  const currentReviewActivity = currentActive || sessionActivities.find((item) => item.id === selectedReviewActivityId) || currentLatestActivity
  const displayedResponses = (currentReviewActivity && preview?.responsesByActivity?.[currentReviewActivity.id]) || preview?.responses || responses
  const displayedResponseCount = preview
    ? (currentReviewActivity && preview.responsesByActivity?.[currentReviewActivity.id]?.length) ?? preview.responseCount
    : responseCount
  const sessionResponseCount = sessionActivities.reduce((total, item) => total + (item.response_count || 0), 0)

  const load = useCallback(async (silent = false) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (preview) return
    if (loadRef.current) {
      pendingLoadRef.current = true
      return
    }
    if (!meetingId || !panelToken) return
    do {
      pendingLoadRef.current = false
      loadRef.current = true
      try {
        const payload = await meetings.getMeetingInspirePanel(meetingId, panelToken)
      if (!ownsTool()) return;
        if (payload.role !== "presenter") throw new Error("Presenter access is required.")
        const nextDecks = payload.decks || []
        const nextActive = payload.active_activity || null
        const nextLatest = payload.latest_activity || nextActive
        const nextActivities = payload.activities || (nextLatest ? [nextLatest] : [])
        const nextResponses = payload.responses || []
        const nextResponseCount = payload.responses_count || 0
        const retainedReviewId = !nextActive && nextActivities.some((item) => item.id === selectedReviewActivityIdRef.current)
          ? selectedReviewActivityIdRef.current
          : ""
        const nextReviewId = nextActive?.id || retainedReviewId || nextLatest?.id || ""
        const payloadResponseActivityId = nextActive?.id || nextLatest?.id || ""
        setDecks(nextDecks)
        setDeckId((current) => (
          current === CUSTOM_DECK_ID || nextDecks.some((deck) => deck.id === current)
            ? current
            : nextDecks[0]?.id || CUSTOM_DECK_ID
        ))
        setActive(nextActive)
        setActivities(nextActivities)
        setLatestActivity(nextLatest)
        selectedReviewActivityIdRef.current = nextReviewId
        setSelectedReviewActivityId(nextReviewId)
        if (!nextReviewId || nextReviewId === payloadResponseActivityId) {
          setResponses(nextResponses)
          setResponseCount(nextResponseCount)
          setNextPage(nextResponseCount > nextResponses.length ? 2 : null)
        }
        setSharedResponse(payload.shared_response || null)
        setSharedActivityId(payload.shared_activity?.id || null)
        if (nextActive) {
          setShowReview(false)
        } else if (nextReviewId) {
          setShowReview(dismissedReviewIdRef.current !== nextReviewId)
        } else {
          setShowReview(false)
        }
        setRealtimeUrl(payload.realtime_url || null)
        setError("")
      } catch (err: any) {
      if (!ownsTool()) return;
        if (!silent) setError(err?.response?.data?.detail || err?.message || "Unable to load WiseFriend controls.")
      } finally {
      if (!ownsTool()) return;
        loadRef.current = false
        setLoading(false)
      }
    } while (pendingLoadRef.current)
  }, [meetingId, panelToken, preview, captureTool])

  useEffect(() => {
    if (preview) return
    if (!meetingId || !panelToken) return
    void load()
    const id = window.setInterval(() => void load(true), POLL_MS)
    return () => window.clearInterval(id)
  }, [load, meetingId, panelToken, preview])
  useMeetingPanelEvents({ onReconnect: () => void load(true), panelToken, realtimeUrl, enabled: !preview && Boolean(realtimeUrl), onEvent: (event) => { if (event.type.startsWith("inspire.")) void load(true) } })
  const embedRefreshKey = embed?.refreshKey
  useEffect(() => {
    if (!embedRefreshKey) return
    void load(true)
  }, [embedRefreshKey, load])

  useEffect(() => {
    if (!preview) return
    const nextLatest = preview.latestActivity || preview.active || null
    const nextActivities = preview.activities || (nextLatest ? [nextLatest] : [])
    const retainedReviewId = !preview.active && nextActivities.some((item) => item.id === selectedReviewActivityIdRef.current)
      ? selectedReviewActivityIdRef.current
      : ""
    const nextReviewId = preview.active?.id || retainedReviewId || nextLatest?.id || ""
    setActivities(nextActivities)
    setLatestActivity(nextLatest)
    selectedReviewActivityIdRef.current = nextReviewId
    setSelectedReviewActivityId(nextReviewId)
    setResponses((nextReviewId && preview.responsesByActivity?.[nextReviewId]) || preview.responses || [])
    setSharedResponse(preview.sharedResponse || null)
    setSharedActivityId(preview.sharedActivityId || (preview.sharedResponse ? nextLatest?.id || null : null))
    setResponseCount(preview.responseCount)
    setNextPage(null)
    if (preview.active) {
      setShowReview(false)
    } else if (nextReviewId) {
      setShowReview(dismissedReviewIdRef.current !== nextReviewId)
    } else {
      setShowReview(false)
    }
  }, [preview, preview?.active, preview?.activities, preview?.latestActivity, preview?.responseCount, preview?.responses, preview?.responsesByActivity, preview?.sharedActivityId, preview?.sharedResponse])

  useEffect(() => {
    if (embed) return
    const handleAck = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isInspireControlAckMessage(event.data)) return
      if (event.data.meetingId !== meetingId) return
      if (ackTimerRef.current) window.clearTimeout(ackTimerRef.current)
      ackTimerRef.current = null
      setPending(null)
      if (!event.data.accepted) setError("The Daily call could not apply that action.")
    }
    window.addEventListener("message", handleAck)
    return () => { window.removeEventListener("message", handleAck); if (ackTimerRef.current) window.clearTimeout(ackTimerRef.current) }
  }, [embed, meetingId])

  const postControl = (action: InspireControlAction, activityId: string) => {
    if (!meetingId) return
    if (embed) {
      embed.onControlAction(action, activityId)
        .then((accepted) => {
          setPending(null)
          if (!accepted) setError("The call could not apply that action.")
        })
        .catch(() => {
          setPending(null)
          setError("The call could not apply that action.")
        })
      return
    }
    window.top?.postMessage({ type: INSPIRE_CONTROL_MESSAGE_TYPE, meetingId, activityId, action }, window.location.origin)
    ackTimerRef.current = window.setTimeout(() => {
      setPending(null)
      setError("The meeting did not respond. Open this panel inside the Daily call and try again.")
    }, ACK_TIMEOUT_MS)
  }

  const launch = async (closeOpenPoll = false) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (pending) return
    const normalizedCustomPrompt = customPrompt.trim()
    if (isCustom && !normalizedCustomPrompt) return
    if (!isCustom && (!selectedDeck || !selectedCard)) return
    if (preview) {
      preview.onLaunch(isCustom
        ? { kind: "custom", customPrompt: normalizedCustomPrompt, visibility }
        : { kind: "card", deckId: selectedDeck!.id, cardId: selectedCard!.id, visibility })
      return
    }
    if (!meetingId) return
    setPending("start")
    setError("")
    setConfirmClosePoll(false)
    try {
      const activity = await meetings.createMeetingInspireActivity(
        meetingId,
        panelToken,
        {
          ...(isCustom
            ? { custom_prompt: normalizedCustomPrompt, visibility_mode: visibility }
            : { deck_id: selectedDeck!.id, card_id: selectedCard!.id, visibility_mode: visibility }),
          ...(closeOpenPoll ? { close_open_poll: true } : {}),
        },
      )
      if (!ownsTool()) return;
      dismissedReviewIdRef.current = null
      setActive(activity)
      setActivities((current) => [activity, ...current.filter((item) => item.id !== activity.id)])
      setLatestActivity(activity)
      selectedReviewActivityIdRef.current = activity.id
      setSelectedReviewActivityId(activity.id)
      setResponses([])
      setSharedResponse(null)
      setSharedActivityId(null)
      setResponseCount(0)
      setNextPage(null)
      setShowReview(false)
      postControl("start", activity.id)
    } catch (err: any) {
      if (!ownsTool()) return;
      setPending(null)
      if (err?.response?.status === 409 && err?.response?.data?.code === "poll_open" && !closeOpenPoll) {
        setConfirmClosePoll(true)
        return
      }
      setError(err?.response?.data?.detail || err?.message || "Unable to launch this Inspire Card.")
    }
  }

  const drawRandomCard = () => {
    const cards = selectedDeck?.cards || []
    if (cards.length === 0 || isShuffling) return
    const alternatives = cards.length > 1 ? cards.filter((card) => card.id !== selectedCard?.id) : cards
    const next = alternatives[Math.floor(Math.random() * alternatives.length)]
    if (next) {
      if (shuffleTimerRef.current) window.clearTimeout(shuffleTimerRef.current)
      setIsShuffling(true)
      setShuffleCycle((cycle) => cycle + 1)
      setCardId(next.id)
      const prefersReducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      shuffleTimerRef.current = window.setTimeout(() => {
        setIsShuffling(false)
        shuffleTimerRef.current = null
      }, prefersReducedMotion ? 0 : 1000)
    }
  }

  useEffect(() => () => {
    if (shuffleTimerRef.current) window.clearTimeout(shuffleTimerRef.current)
  }, [])

  const end = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const currentActive = preview?.active || active
    if (!currentActive || pending) return
    if (preview) {
      preview.onEnd()
      return
    }
    if (!meetingId) return
    setPending("stop")
    setError("")
    try {
      const ended = await meetings.endMeetingInspireActivity(meetingId, currentActive.id, panelToken)
      if (!ownsTool()) return;
      setActive(null)
      setActivities((current) => [ended, ...current.filter((item) => item.id !== ended.id)])
      setLatestActivity(ended)
      selectedReviewActivityIdRef.current = ended.id
      setSelectedReviewActivityId(ended.id)
      setSharedResponse(null)
      setSharedActivityId(null)
      setShowReview(true)
      dismissedReviewIdRef.current = null
      postControl("stop", ended.id)
      void load(true)
    } catch (err: any) {
      if (!ownsTool()) return;
      setPending(null)
      setError(err?.response?.data?.detail || err?.message || "Unable to end this Inspire Card.")
    }
  }

  const selectReviewActivity = async (activityId: string) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const nextActivity = sessionActivities.find((item) => item.id === activityId)
    if (!nextActivity || nextActivity.id === selectedReviewActivityId || loadingReview) return
    dismissedReviewIdRef.current = null
    selectedReviewActivityIdRef.current = nextActivity.id
    setSelectedReviewActivityId(nextActivity.id)
    setShowReview(true)
    setResponseCount(nextActivity.response_count || 0)
    setNextPage(null)
    if (preview) return
    if (!meetingId) return
    setLoadingReview(true)
    setResponses([])
    setError("")
    try {
      const page = await meetings.getMeetingInspireResponses(meetingId, nextActivity.id, panelToken, 1)
      if (!ownsTool()) return;
      setResponses(page.results)
      setResponseCount(page.count)
      setNextPage(page.next_page || null)
    } catch (err: any) {
      if (!ownsTool()) return;
      setError(err?.response?.data?.detail || "Unable to load this card's responses.")
    } finally {
      if (!ownsTool()) return;
      setLoadingReview(false)
    }
  }

  const loadMoreResponses = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const reviewActivity = currentReviewActivity
    if (preview || !meetingId || !reviewActivity || !nextPage || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await meetings.getMeetingInspireResponses(
        meetingId,
        reviewActivity.id,
        panelToken,
        nextPage,
      )
      if (!ownsTool()) return;
      setResponses((current) => [...current, ...page.results])
      setResponseCount(page.count)
      setNextPage(page.next_page || null)
    } catch (err: any) {
      if (!ownsTool()) return;
      setError(err?.response?.data?.detail || "Unable to load more responses.")
    } finally {
      if (!ownsTool()) return;
      setLoadingMore(false)
    }
  }

  const shareResponse = async (item: MeetingInspireResponse) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const reviewActivity = currentReviewActivity
    if (!reviewActivity || reviewActivity.status !== "ended" || pending) return
    if (preview) {
      setSharedResponse(item)
      setSharedActivityId(reviewActivity.id)
      preview.onShareResponse?.(item, reviewActivity)
      return
    }
    if (!meetingId) return
    setPending("share")
    setError("")
    try {
      const result = await meetings.shareMeetingInspireResponse(meetingId, reviewActivity.id, panelToken, item.id)
      if (!ownsTool()) return;
      setSharedResponse(result.shared_response)
      setSharedActivityId(result.activity.id)
      postControl("share", reviewActivity.id)
    } catch (err: any) {
      if (!ownsTool()) return;
      setPending(null)
      setError(err?.response?.data?.detail || err?.message || "Unable to share this response.")
    }
  }

  const stopSharing = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const sharingActivity = sessionActivities.find((item) => item.id === sharedActivityId) || currentReviewActivity
    if (!sharingActivity || !sharedResponse || pending) return
    if (preview) {
      setSharedResponse(null)
      setSharedActivityId(null)
      preview.onStopSharing?.()
      return
    }
    if (!meetingId) return
    setPending("unshare")
    setError("")
    try {
      await meetings.shareMeetingInspireResponse(meetingId, sharingActivity.id, panelToken, null)
      if (!ownsTool()) return;
      setSharedResponse(null)
      setSharedActivityId(null)
      postControl("unshare", sharingActivity.id)
    } catch (err: any) {
      if (!ownsTool()) return;
      setPending(null)
      setError(err?.response?.data?.detail || err?.message || "Unable to stop sharing this response.")
    }
  }

  const activeAccent = currentActive?.deck_accent_color_snapshot || "#1F3A5F"
  const activeInk = cardInk(activeAccent)
  const reviewAccent = currentReviewActivity?.deck_accent_color_snapshot || "#1F3A5F"
  const reviewInk = cardInk(reviewAccent)

  return (
    <div data-testid="wisefriend-controls-panel" data-embedded={embed ? "true" : undefined} className={`${preview || embed ? "absolute" : "fixed z-50"} inset-0 flex min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top_left,_#fff8e9,_#f2e6ce_52%,_#ded0b6)] text-[#263a5b]`}>
      {/* In the call the Inspire Cards tool header already names this panel; a second header would box it twice. */}
      {!embed && <header className="border-b border-[#d8c7a7] bg-[#fffdf8]/95 px-3 py-2.5 shadow-[0_1px_0_rgba(76,59,34,0.05)] sm:px-4 sm:py-4">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <span className="flex h-8 w-8 rotate-[-3deg] items-center justify-center rounded-lg bg-[#263a5b] text-amber-200 shadow-sm sm:h-10 sm:w-10 sm:rounded-xl"><Sparkles className="h-4 w-4 sm:h-[18px] sm:w-[18px]" /></span>
          <div className="min-w-0 flex-1"><h1 className="text-sm font-bold text-[#263a5b]">WiseFriend Controls</h1><p className="mt-0.5 text-xs text-[#756951]">Pick a card. Stir the room.</p></div>
          <Button type="button" size="icon" variant="ghost" aria-label="Refresh WiseFriend controls" onClick={() => { if (!preview) void load() }} className="h-8 w-8 min-h-0 p-0 text-[#52627d] hover:bg-[#efe2c8] hover:text-[#263a5b]"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </header>}
      <main className="min-h-0 flex-1 overflow-y-auto p-2.5 sm:p-4">
        <div className="mx-auto w-full max-w-3xl">
        {loading ? <p className="rounded-xl border border-[#d9c9aa] bg-[#fffdf8]/90 px-4 py-6 text-sm text-[#756951] shadow-sm">Shuffling the deck…</p> : currentActive ? (
          <div className="space-y-2.5 sm:space-y-4">
            <div className="relative px-1 py-1.5 sm:px-2 sm:py-3">
              <div data-testid="wisefriend-active-card-backing" aria-hidden="true" className="absolute inset-x-3 inset-y-2 rotate-0 rounded-2xl opacity-30 sm:inset-x-4 sm:inset-y-3 sm:rounded-[1.6rem]" style={{ backgroundColor: activeAccent }} />
              <article data-testid="wisefriend-active-physical-card" className="relative flex h-[176px] rotate-0 flex-col overflow-hidden rounded-2xl border border-black/10 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.35)] sm:h-auto sm:min-h-[250px] sm:rounded-[1.6rem] sm:p-5" style={{ backgroundColor: activeAccent, color: activeInk }}>
                <div className="flex items-center justify-between gap-3 text-[10px] font-black uppercase tracking-[0.18em]"><span>{currentActive.deck_name_snapshot || "Inspire Deck"}</span><span className="rounded-full bg-black/10 px-2 py-1">Live</span></div>
                <h2 className="mt-4 line-clamp-4 text-lg font-black leading-snug sm:mt-8 sm:line-clamp-none sm:text-xl">{currentActive.card_prompt_snapshot}</h2>
                {currentActive.card_helper_text_snapshot && <p className="mt-2 line-clamp-2 text-[11px] leading-4 opacity-75 sm:mt-4 sm:line-clamp-none sm:text-xs sm:leading-5">{currentActive.card_helper_text_snapshot}</p>}
                <p className="mt-auto pt-3 text-[9px] font-bold uppercase tracking-widest opacity-55 sm:pt-8 sm:text-[10px]">WiseFriend Inspire Card</p>
              </article>
            </div>
            <div className="rounded-2xl border border-[#d9c9aa] bg-[#fffdf8]/90 p-3 shadow-sm sm:p-4">
              <div className="flex items-center justify-between"><span className="text-xs text-[#756951]">Cards on the table</span><strong className="text-sm text-[#263a5b]">{displayedResponseCount} {displayedResponseCount === 1 ? "response" : "responses"}</strong></div>
              <p className="mt-1 text-[11px] leading-4 text-[#8b7d63] sm:text-xs">{currentActive.visibility_mode === "anonymous" ? "Names are tucked away from presenters." : "Responses arrive with names attached."}</p>
              <Button type="button" aria-label="End for everyone" onClick={end} disabled={Boolean(pending)} className="mt-2.5 min-h-10 w-full bg-red-500 text-white hover:bg-red-400 sm:mt-4"><Square className="h-4 w-4" />{pending === "stop" ? "Ending…" : "Put the card away"}</Button>
            </div>
            <div className="space-y-3 rounded-2xl border border-[#d9c9aa] bg-[#fffdf8]/80 p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-[#52627d]">Live responses</h3>
                <span className="text-[10px] text-[#8b7d63]">Newest first</span>
              </div>
              {displayedResponses.length === 0 ? (
                <p className="rounded-xl border border-dashed border-[#cdbd9e] bg-white/55 px-3 py-6 text-center text-xs leading-5 text-[#756951]">Responses will land here as people submit them.</p>
              ) : (
                <div className="space-y-3" aria-live="polite">
                  {displayedResponses.map((item) => (
                    <article key={item.id} className="rounded-2xl border border-[#ded1b8] bg-white/85 p-4 shadow-sm">
                      <div className="mb-2 flex items-baseline justify-between gap-3">
                        <h4 className="min-w-0 truncate text-xs font-bold text-[#263a5b]">{item.display_name}</h4>
                        <time className="shrink-0 text-[10px] text-[#8b7d63]" dateTime={item.updated_at}>{new Date(item.updated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</time>
                      </div>
                      <p className="whitespace-pre-wrap text-xs leading-5 text-[#394a65]">{item.body}</p>
                    </article>
                  ))}
                </div>
              )}
              {nextPage && <Button type="button" variant="outline" onClick={loadMoreResponses} disabled={loadingMore} className="w-full border-[#cdbd9e] bg-white/70 text-[#263a5b] hover:bg-[#fff4d2]">{loadingMore ? "Loading…" : "Load more responses"}</Button>}
            </div>
          </div>
        ) : showReview && currentReviewActivity ? (
          <div data-testid="inspire-closed-response-review" className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#2f6c5d]">Responses closed</p>
                <h2 className="mt-1 text-base font-bold text-[#263a5b]">Session responses</h2>
                <p className="mt-1 text-xs leading-5 text-[#756951]">Browse every card and answer from this call, then share one back with the group.</p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Return to deck selection"
                onClick={() => {
                  dismissedReviewIdRef.current = currentReviewActivity.id
                  setShowReview(false)
                }}
                className="h-8 w-8 min-h-0 shrink-0 p-0 text-[#52627d] hover:bg-[#efe2c8] hover:text-[#263a5b]"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="rounded-xl border border-[#d9c9aa] bg-[#fffdf8]/80 p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-3">
                <label htmlFor="inspire-session-activity" className="text-[10px] font-black uppercase tracking-[0.14em] text-[#52627d]">Card from this session</label>
                <span className="text-[10px] text-[#8b7d63]">{sessionResponseCount} {sessionResponseCount === 1 ? "answer" : "answers"} · {sessionActivities.length} {sessionActivities.length === 1 ? "card" : "cards"}</span>
              </div>
              <select
                id="inspire-session-activity"
                value={currentReviewActivity.id}
                onChange={(event) => void selectReviewActivity(event.target.value)}
                disabled={loadingReview || Boolean(pending)}
                className="w-full rounded-lg border border-[#cdbd9e] bg-white px-3 py-2.5 text-xs text-[#263a5b] shadow-sm"
              >
                {sessionActivities.map((item) => (
                  <option key={item.id} value={item.id}>{item.response_count || 0} {item.response_count === 1 ? "answer" : "answers"} · {item.card_prompt_snapshot}</option>
                ))}
              </select>
            </div>
            <article className="rotate-0 rounded-[1.35rem] border border-black/10 p-4 shadow-lg" style={{ backgroundColor: reviewAccent, color: reviewInk }}>
              <div className="flex items-center justify-between gap-3 text-[9px] font-black uppercase tracking-[0.16em] opacity-70"><span>{currentReviewActivity.deck_name_snapshot || "Inspire Deck"}</span><span>Closed</span></div>
              <p className="mt-5 text-base font-black leading-snug">{currentReviewActivity.card_prompt_snapshot}</p>
            </article>
            <div className="flex items-center justify-between border-y border-[#d8c7a7] py-3">
              <span className="text-xs text-[#756951]">Answers on the table</span>
              <strong className="text-sm text-[#263a5b]">{displayedResponseCount} {displayedResponseCount === 1 ? "response" : "responses"}</strong>
            </div>
            {sharedResponse && (
              <div className="rounded-2xl border border-[#dfb850] bg-[#fff3c6] p-3 text-xs text-[#5e4a1f] shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 font-bold"><Share2 className="h-4 w-4 shrink-0" /><span className="truncate">{sharedActivityId === currentReviewActivity.id ? `Sharing ${sharedResponse.display_name}'s response` : "Sharing a response from another card"}</span></span>
                  <Button type="button" size="icon" variant="ghost" aria-label="Stop sharing response" onClick={() => void stopSharing()} disabled={Boolean(pending)} className="h-7 w-7 min-h-0 shrink-0 p-0 text-[#6f5520] hover:bg-amber-200/60"><X className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
            {loadingReview ? (
              <div className="rounded-2xl border border-dashed border-[#cdbd9e] bg-white/55 px-4 py-8 text-center text-xs leading-5 text-[#756951]">Gathering this card&apos;s responses…</div>
            ) : displayedResponses.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#cdbd9e] bg-white/55 px-4 py-8 text-center text-xs leading-5 text-[#756951]">No one submitted an answer for this card.</div>
            ) : (
              <div className="space-y-3" aria-live="polite">
                {displayedResponses.map((item) => (
                  <button key={item.id} type="button" aria-label={`Share response from ${item.display_name} with the group`} onClick={() => void shareResponse(item)} disabled={Boolean(pending)} className={`w-full rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-[#fff9e9] disabled:cursor-wait disabled:opacity-70 ${sharedActivityId === currentReviewActivity.id && sharedResponse?.id === item.id ? "border-[#dfb850] bg-[#fff3c6]" : "border-[#ded1b8] bg-white/85"}`}>
                    <div className="mb-2 flex items-baseline justify-between gap-3">
                      <h3 className="min-w-0 truncate text-xs font-bold text-[#263a5b]">{item.display_name}</h3>
                      <time className="shrink-0 text-[10px] text-[#8b7d63]" dateTime={item.updated_at}>{new Date(item.updated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</time>
                    </div>
                    <p className="whitespace-pre-wrap text-xs leading-5 text-[#394a65]">{item.body}</p>
                    <span className="mt-3 flex items-center gap-1.5 border-t border-[#ded1b8] pt-3 text-[10px] font-bold uppercase tracking-wider text-[#7a5d15]"><Share2 className="h-3.5 w-3.5" />{sharedActivityId === currentReviewActivity.id && sharedResponse?.id === item.id ? "Shared with group" : "Share with group"}</span>
                  </button>
                ))}
              </div>
            )}
            {nextPage && <Button type="button" variant="outline" onClick={loadMoreResponses} disabled={loadingMore} className="w-full border-[#cdbd9e] bg-white/70 text-[#263a5b] hover:bg-[#fff4d2]">{loadingMore ? "Loading…" : "Load more responses"}</Button>}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                dismissedReviewIdRef.current = currentReviewActivity.id
                setShowReview(false)
              }}
              className="w-full border-[#d9b24c] bg-[#fff3c6] text-[#5e4a1f] hover:bg-[#ffe9a2]"
            >
              <Sparkles className="h-4 w-4" /> Choose the next card
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {sessionActivities.length > 0 && currentLatestActivity && (
              <button type="button" onClick={() => { dismissedReviewIdRef.current = null; setShowReview(true) }} className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#92b9a9] bg-[#e4f2ec] px-3 py-2.5 text-left text-xs text-[#28594d] shadow-sm hover:bg-[#d9ede4]">
                <span className="flex min-w-0 items-center gap-2"><MessageSquareText className="h-4 w-4 shrink-0" /><span className="min-w-0"><span className="block truncate font-semibold">Review session responses</span><span className="mt-0.5 block truncate text-[10px] text-[#527e72]">{sessionResponseCount} {sessionResponseCount === 1 ? "answer" : "answers"} across {sessionActivities.length} {sessionActivities.length === 1 ? "card" : "cards"}</span></span></span>
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider">Browse</span>
              </button>
            )}
            <div><label htmlFor="inspire-deck" className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-[#52627d]"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-full border border-black/10 shadow-sm" style={{ backgroundColor: selectedAccent }} />Choose a deck</label><select id="inspire-deck" value={isCustom ? CUSTOM_DECK_ID : selectedDeck?.id || CUSTOM_DECK_ID} onChange={(event) => { setDeckId(event.target.value); setCardId("") }} disabled={isShuffling} className="w-full rounded-xl border border-[#cdbd9e] bg-white px-3 py-2.5 text-sm text-[#263a5b] shadow-sm disabled:cursor-wait disabled:opacity-70">{availableDecks.map((deck) => <option key={deck.id} value={deck.id}>{deck.name} · {deck.cards.length} cards</option>)}<option value={CUSTOM_DECK_ID}>Custom · Write your own question</option></select></div>
            {isCustom ? (
              <div>
                <label htmlFor="inspire-custom-question" className="mb-1.5 block text-xs font-semibold text-[#52627d]">Your question</label>
                <Textarea id="inspire-custom-question" value={customPrompt} maxLength={500} onChange={(event) => setCustomPrompt(event.target.value)} placeholder="What would you like to ask the room?" className="min-h-[110px] resize-y border-[#cdbd9e] bg-white text-sm text-[#263a5b] shadow-sm placeholder:text-[#9a8d76]" />
                <p className="mt-1 text-right text-[10px] text-[#8b7d63]">{customPrompt.length}/500</p>
              </div>
            ) : (
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2"><div><label htmlFor="inspire-card" className="mb-1.5 block text-xs font-semibold text-[#52627d]">Choose a card</label><select id="inspire-card" value={selectedCard?.id || ""} onChange={(event) => setCardId(event.target.value)} disabled={isShuffling} className="w-full rounded-xl border border-[#cdbd9e] bg-white px-3 py-2.5 text-sm text-[#263a5b] shadow-sm disabled:cursor-wait disabled:opacity-70">{selectedDeck?.cards.map((card, index) => <option key={card.id} value={card.id}>Card {index + 1}: {card.prompt}</option>)}</select></div><Button type="button" variant="outline" aria-label="Shuffle and draw a random card" onClick={drawRandomCard} disabled={!selectedDeck?.cards.length || isShuffling} className="h-[42px] min-h-0 border-[#cdbd9e] bg-white/80 px-3 text-[#263a5b] shadow-sm hover:bg-[#fff4d2]"><Shuffle data-testid="inspire-shuffle-icon" className="h-4 w-4" /><span className="sr-only sm:not-sr-only">{isShuffling ? "Shuffling…" : "Shuffle & draw"}</span></Button></div>
            )}
            {(selectedCard || isCustom) && (
              <div className="relative px-2 py-3">
                <div data-testid="inspire-card-preview-backing" aria-hidden="true" className="absolute inset-x-4 inset-y-3 rotate-0 rounded-[1.6rem] opacity-30" style={{ backgroundColor: selectedAccent }} />
                {isShuffling && (
                  <div data-testid="inspire-playing-card-shuffle" aria-label="Shuffling the deck" className="absolute inset-0 z-20 flex items-center justify-center overflow-hidden rounded-[1.6rem] bg-[#fff8e9]/70 backdrop-blur-[1px]">
                    <div className="relative h-[205px] w-[230px] [perspective:900px] motion-safe:animate-inspire-deck-square" aria-hidden="true">
                      {Array.from({ length: 8 }, (_, index) => {
                        const packet = index % 2 === 0 ? "left" : "right"
                        const layer = Math.floor(index / 2)
                        return (
                          <span key={index} className="absolute left-1/2 top-1/2 -ml-[52px] -mt-[75px]" style={{ zIndex: index + 1 }}>
                            <span
                              data-testid="inspire-playing-card"
                              className={`block h-[150px] w-[104px] origin-bottom rounded-[0.8rem] border-2 border-white/70 p-1.5 shadow-[0_8px_20px_rgba(0,0,0,0.45)] [backface-visibility:hidden] [transform-style:preserve-3d] ${packet === "left" ? "motion-safe:animate-inspire-riffle-left" : "motion-safe:animate-inspire-riffle-right"}`}
                              style={{ backgroundColor: selectedAccent, animationDelay: `${layer * 45}ms` }}
                            >
                              <span className="flex h-full w-full items-center justify-center rounded-[0.55rem] border border-white/45 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.18)_0,rgba(255,255,255,0.18)_3px,rgba(0,0,0,0.08)_3px,rgba(0,0,0,0.08)_6px)]">
                                <Sparkles className="h-7 w-7 rounded-full bg-black/15 p-1.5 text-white/90" />
                              </span>
                            </span>
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}
                <article key={`${selectedCard?.id || "custom"}-${shuffleCycle}`} data-testid="inspire-card-preview" aria-live="polite" aria-busy={isShuffling} className={`relative flex min-h-[280px] rotate-0 flex-col rounded-[1.6rem] border border-black/10 p-5 shadow-[0_18px_45px_rgba(0,0,0,0.35)] ${shuffleCycle > 0 ? "motion-safe:animate-inspire-card-reveal" : ""}`} style={{ backgroundColor: selectedAccent, color: selectedInk, animationDelay: shuffleCycle > 0 ? "900ms" : undefined }}>
                  <div className="flex items-center justify-between gap-3 text-[10px] font-black uppercase tracking-[0.18em]"><span>{isCustom ? "Custom" : selectedDeck?.name}</span><Sparkles className="h-4 w-4 opacity-60" /></div>
                  <h2 className={`mt-8 text-xl font-black leading-snug ${isCustom && !customPrompt.trim() ? "opacity-45" : ""}`}>{isCustom ? customPrompt.trim() || "Your question will look like this…" : selectedCard?.prompt}</h2>
                  {!isCustom && selectedCard?.helper_text && <p className="mt-4 text-xs leading-5 opacity-75">{selectedCard.helper_text}</p>}
                  {!isCustom && selectedCard?.facilitator_notes && <div className="mt-5 rotate-[1deg] rounded-xl bg-white/80 px-3 py-3 text-[#283248] shadow-sm"><p className="text-[9px] font-black uppercase tracking-wider text-[#6f5c42]">For your eyes only{selectedCard.uses_deck_facilitator_notes ? " · from the deck" : ""}</p><p className="mt-1 text-xs leading-5">{selectedCard.facilitator_notes}</p></div>}
                  <p className="mt-auto pt-8 text-[10px] font-bold uppercase tracking-widest opacity-55">Take a breath. Then ask.</p>
                </article>
              </div>
            )}
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold text-[#52627d]">How should responses arrive?</legend>
              {(["named", "anonymous"] as const).map((mode) => (
                <label key={mode} className={`grid cursor-pointer grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 rounded-xl border p-3 shadow-sm ${visibility === mode ? "border-[#d7aa35] bg-[#fff3c6]" : "border-[#d9c9aa] bg-[#fffdf8]/80"}`}>
                  <span data-testid={`inspire-visibility-leading-${mode}`} className="flex h-5 items-center gap-2">
                    <input className="m-0 h-4 w-4 shrink-0 accent-[#d39a15]" type="radio" name="visibility" value={mode} checked={visibility === mode} onChange={() => setVisibility(mode)} />
                    {mode === "named" ? <Eye className="h-4 w-4 shrink-0 text-[#8a6712]" /> : <EyeOff className="h-4 w-4 shrink-0 text-[#8a6712]" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-5 text-[#263a5b]">{mode === "named" ? "Names on the cards" : "Anonymous to presenters"}</span>
                    <span className="mt-0.5 block text-xs leading-4 text-[#756951]">{mode === "named" ? "Show participant display names." : "WiseFriend keeps response ownership, but presenters never receive identity."}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {confirmClosePoll && (
              <div role="alertdialog" aria-labelledby="inspire-close-poll-title" data-testid="inspire-close-poll-confirm" className="rounded-xl border border-[#d9b24c] bg-[#fff3c6] p-3 text-[#5e4a1f] shadow-sm">
                <p id="inspire-close-poll-title" className="text-sm font-semibold">A poll is still open</p>
                <p className="mt-1 text-xs leading-5">Close the poll to deal this card? Voting stops for everyone and the votes so far are kept.</p>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" size="auto" onClick={() => setConfirmClosePoll(false)} className="min-h-9 border-[#d9b24c] bg-white/70 px-3 text-xs text-[#5e4a1f]">Keep the poll open</Button>
                  <Button type="button" size="auto" onClick={() => void launch(true)} disabled={Boolean(pending)} className="min-h-9 bg-[#263a5b] px-3 text-xs text-white hover:bg-[#1d2d47]">Close poll and deal</Button>
                </div>
              </div>
            )}
            <Button type="button" aria-label="Launch for everyone" onClick={() => void launch()} disabled={Boolean(pending) || (isCustom ? !customPrompt.trim() : !selectedCard)} className="w-full bg-[#f5c84c] text-[#263a5b] shadow-sm hover:bg-[#f8d56d]"><Play className="h-4 w-4" />{pending === "start" ? "Dealing the card…" : isCustom ? "Ask everyone" : "Deal this card to everyone"}</Button>
          </div>
        )}
        {error && <p role="alert" className="mt-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">{error}</p>}
        </div>
      </main>
    </div>
  )
}

function WiseFriendControlsPanel(props: { preview?: WiseFriendControlsPreview; embed?: InspireEmbedContext }) {
  const { meetingId } = useParams<{ meetingId: string }>()
  const [params] = useSearchParams()
  const owner = useToolOwnership(`${meetingId}:${params.get('panel_token') || ''}`)
  return <ControlsSession key={owner.key} {...props} />
}
export default WiseFriendControlsPanel
